import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  styled,
  CircularProgress,
} from "@mui/material";
import ContentUsBgImage from "@assets/ContentUsBgImage.png";
import { sendOTP } from "@services/auth";
import { useNavigate, useLocation } from "react-router-dom";
import {
  authPanelTaglineForVertical,
  landingVerticalFromSearch,
} from "@utils/appPath";

const SignUpContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  // width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  padding: "16px",
  [theme.breakpoints.down("sm")]: {
    padding: "8px",
    alignItems: "flex-start",
    paddingTop: "20px",
  },
}));

const ContactSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 0px",
  position: "relative",
  width: "100%",
  maxWidth: "900px",
  margin: "0 auto",
  gap: 16,
  [theme.breakpoints.down("lg")]: {
    gap: 12,
    flexDirection: "column",
    padding: "12px",
    maxWidth: "900px",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    padding: "16px 8px",
    gap: 10,
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px 6px",
    gap: 8,
  },
}));

const BackgroundOverlay = styled(Box)({
  position: "absolute",
  height: "100%",
  left: 0,
  top: 0,
  width: "100%",
  backgroundImage: `
    radial-gradient(ellipse 42.02px 68.593px at 298.8px 124.35px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 77.28px 6.0674px at 1471.2px 363.44px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 1.62px 57.671px at 682.2px 879.48px, rgba(197,255,225,0.4) 0%, rgba(255,255,255,0) 100%)
  `,
});
const GradientBackground = styled(Box)({
  position: "absolute",
  inset: 0,
  background:
    "radial-gradient(ellipse at 138px 40px, #065b70 0%, #0a829d 100%)",
  opacity: 0.9,
  backgroundImage: `url(${ContentUsBgImage})`,
  width: "100%",
  height: "100%",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
});

const LeftPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "620px",
  height: "520px",
  backgroundImage: `url(${ContentUsBgImage})`,
  borderRadius: "32px",
  border: "1px solid #ffffff",
  overflow: "hidden",
  marginRight: "-80px",
  flexShrink: 0,
  [theme.breakpoints.down("lg")]: {
    width: "480px",
    height: "400px",
    marginRight: "-20px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "500px",
    height: "auto",
    minHeight: "240px",
    aspectRatio: "16 / 10",
    marginRight: 0,
    borderRadius: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    minHeight: "240px",
    borderRadius: "16px",
  },
}));

const LeftPanelContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "95.5px",
  top: "101px",
  width: "480px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    left: "32px",
    top: "48px",
    width: "380px",
  },
  [theme.breakpoints.down("md")]: {
    position: "relative",
    left: 0,
    top: 0,
    width: "100%",
    padding: "16px 12px 12px",
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 12px 12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "64px",
  lineHeight: "1.1",
  marginBottom: "40px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    fontSize: "42px",
    marginBottom: "24px",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "36px",
    marginBottom: "12px",
    lineHeight: "1.2",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
    marginBottom: "12px",
    paddingTop: "48px",
    textAlign: "center",
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "1.5",
  color: "#ffffff",
  width: "422px",
  textAlign: "center",
  whiteSpace: "normal",
  overflowWrap: "normal",
  wordBreak: "normal",
  hyphens: "none",
  [theme.breakpoints.down("lg")]: {
    width: "360px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    fontSize: "16px",
    lineHeight: "1.45",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    lineHeight: "1.4",
    maxWidth: 300,
    margin: "0 auto",
    textAlign: "center",
    whiteSpace: "normal",
    overflowWrap: "normal",
    wordBreak: "normal",
    hyphens: "none",
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  border: "1px solid #05495a",
  borderRadius: "16px",
  padding: "32px 28px 32px 32px",
  width: "480px",
  position: "relative",
  flexShrink: 0,
  minHeight: 500,
  [theme.breakpoints.down("lg")]: {
    width: "420px",
    padding: "28px 24px 28px 28px",
    minHeight: 540,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "480px",
    padding: "24px 18px 24px 24px",
    minHeight: 520,
  },
  [theme.breakpoints.down("sm")]: {
    padding: "20px 14px 20px 20px",
    borderRadius: "12px",
    margin: "0 6px",
    maxWidth: "90%",
    minHeight: 500,
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  width: "100%",
  height: "100%",
  [theme.breakpoints.down("sm")]: {
    gap: "10px",
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "32px",
  alignItems: "flex-start",
  [theme.breakpoints.down("sm")]: {
    gap: "10px",
  },
}));

const BackButton = styled(Button)({
  minWidth: "32px",
  width: "32px",
  height: "32px",
  padding: 0,
  color: "#033843",
  "&:hover": {
    backgroundColor: "rgba(3, 56, 67, 0.04)",
  },
});

const PageTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#033843",
  height: "35px",
  width: "221px",
});

const PageSubtitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "20px",
  lineHeight: "1.5",
  color: "#262626",
});

const InputContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4.5px",
  width: "100%",
});

const InputLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    height: "42px",
    borderRadius: "4px",
    "& fieldset": {
      borderColor: "#e6fafc",
    },
    "&:hover fieldset": {
      borderColor: "#bfe5ec",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0a829d",
    },
    boxShadow:
      "inset 0px 0px 8px 0px rgba(0,108,116,0.12), inset 0px 0px 4px 0px rgba(34,110,127,0.25)",
  },
  "& .MuiOutlinedInput-input": {
    padding: "11px 14px",
    fontSize: "14px",
    fontFamily: "Rethink Sans, sans-serif",
  },
});

const SendOTPButton = styled(Button)<{ isActive: boolean }>(({ isActive }) => ({
  backgroundColor: isActive
    ? "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)"
    : "#ddf0f4",
  border: "1px solid #ffffff",
  borderRadius: "60px",
  height: "60px",
  width: "100%",
  padding: "12px 24px",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "22px",
  color: isActive ? "#ffffff" : "#bad6dc",
  background: isActive
    ? "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)"
    : "#ddf0f4",
  boxShadow: isActive
    ? "0px 4px 24px 0px rgba(10,130,157,0.2), 0px 4px 7.3px 0px rgba(133,233,255,0.4)"
    : "none",
  "&:hover": {
    background: isActive
      ? "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)"
      : "#ddf0f4",
  },
  "&:disabled": {
    background: "#ddf0f4",
    color: "#bad6dc",
  },
}));

const FormContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "35px",
  alignItems: "center",
  padding: "24px 0",
  flex: 1,
  width: "100%",
});

const InputSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  width: "100%",
});

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const landingVertical = landingVerticalFromSearch(location.search);
  const panelTagline = authPanelTaglineForVertical(landingVertical);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate(
      landingVertical ? `/login?vertical=${landingVertical}` : "/login",
    );
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const response = await sendOTP(email);
      if (response.success) {
        setSuccess("OTP sent successfully to your email address");
        setTimeout(() => {
          navigate("/verify-otp", { state: { email } });
        }, 1500);
      } else {
        setError(response.message || "Failed to send OTP");
      }
    } catch (err: any) {
      let errorMessage = "Failed to send OTP. Please try again.";
      try {
        const parsed = JSON.parse(err.message);
        errorMessage = parsed?.detail || parsed?.message || errorMessage;
      } catch {
        errorMessage = err.message || errorMessage;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isButtonActive = email.trim() !== "" && isValidEmail(email);

  return (
    <SignUpContainer>
      <ContactSection>
        <BackgroundOverlay />

        <LeftPanel>
          <GradientBackground></GradientBackground>
          <LeftPanelContent>
            <MainTitle>Welcome Back to UniComply</MainTitle>
            <SubTitle>{panelTagline}</SubTitle>
          </LeftPanelContent>
        </LeftPanel>
        <RightPanel>
          <FormContainer>
            <HeaderContainer>
              <BackButton onClick={handleBack}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
                    fill="currentColor"
                  />
                </svg>
              </BackButton>
              <PageTitle>Forgot Password</PageTitle>
            </HeaderContainer>

            <PageSubtitle>
              Enter your registered email to reset your password
            </PageSubtitle>

            <FormContent>
              <InputSection>
                <Box
                  component="form"
                  onSubmit={onSubmit}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    width: "100%",
                  }}
                >
                  <InputContainer>
                    <InputLabel>Email</InputLabel>
                    <StyledTextField
                      fullWidth
                      type="email"
                      variant="outlined"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                    />
                  </InputContainer>
                </Box>
              </InputSection>
            </FormContent>

            {error && (
              <Typography color="error" variant="body2" sx={{ mt: -2 }}>
                {error}
              </Typography>
            )}

            {success && (
              <Typography color="success.main" variant="body2" sx={{ mt: -2 }}>
                {success}
              </Typography>
            )}

            <SendOTPButton
              type="submit"
              disabled={loading || !isButtonActive}
              isActive={isButtonActive}
              onClick={onSubmit}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CircularProgress size={20} color="inherit" />
                  SENDING OTP...
                </Box>
              ) : (
                "SEND OTP"
              )}
            </SendOTPButton>
          </FormContainer>
        </RightPanel>
      </ContactSection>
    </SignUpContainer>
  );
}
